import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { AlertCircle, ShieldCheck, LogOut, Check } from "lucide-react";
import { api } from "@/lib/api";
import { CondicionesUsoContenido } from "./CondicionesUsoContenido";

interface CondicionesEstado {
  requiereAceptar: boolean;
  version: number;
  titulo: string;
  codigoDocumento: string | null;
  fechaPublicacion: string;
  ultimaAceptacionFecha: string | null;
}

/**
 * Modal bloqueante que aparece tras el login si el usuario no ha aceptado la
 * versión vigente del Aviso de Privacidad. No se puede cerrar con escape ni
 * click fuera; las únicas salidas son aceptar o cerrar sesión.
 *
 * Diseño: el botón "Aceptar y continuar" queda deshabilitado hasta que el
 * usuario marca el checkbox de declaración. Adicionalmente, el checkbox sólo
 * se habilita cuando el usuario hizo scroll hasta el final del documento —
 * patrón habitual en términos legales para reforzar la lectura efectiva.
 */
export function CondicionesUsoModal() {
  const auth = useAuth();
  const qc = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [leidoHastaElFinal, setLeidoHastaElFinal] = useState(false);
  const [acepto, setAcepto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = useQuery<CondicionesEstado>({
    queryKey: ["me-condiciones"],
    queryFn: async () => (await api.get<CondicionesEstado>("/api/me/condiciones")).data,
    enabled: auth.isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  const aceptar = useMutation({
    mutationFn: async (version: number) =>
      (await api.post<CondicionesEstado>("/api/me/condiciones/aceptar", { version })).data,
    onSuccess: (data) => {
      qc.setQueryData(["me-condiciones"], data);
      setError(null);
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data?.detail ||
        (e as { response?: { data?: { title?: string } } }).response?.data?.title ||
        (e as Error).message ||
        "No se pudo registrar la aceptación.";
      setError(msg);
    },
  });

  // Reinicia el estado del checkbox cuando llega nueva versión.
  useEffect(() => {
    setAcepto(false);
    setLeidoHastaElFinal(false);
  }, [q.data?.version]);

  if (!auth.isAuthenticated || !q.data || !q.data.requiereAceptar) {
    return null;
  }

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const cerca = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
    if (cerca && !leidoHastaElFinal) {
      setLeidoHastaElFinal(true);
    }
  };

  const cerrarSesion = () => {
    auth.signoutRedirect();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cond-uso-titulo"
    >
      <div className="flex h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-surface shadow-2xl">
        <header className="flex items-center gap-3 border-b bg-primary px-5 py-4 text-white">
          <ShieldCheck className="h-6 w-6 shrink-0" />
          <div className="min-w-0 flex-1">
            <h2 id="cond-uso-titulo" className="text-base font-semibold">
              {q.data.titulo}
            </h2>
            <p className="text-xs text-white/80">
              {q.data.codigoDocumento && <>Código {q.data.codigoDocumento} · </>}
              Versión {q.data.version} · Publicado{" "}
              {new Date(q.data.fechaPublicacion).toLocaleDateString("es-MX")}
            </p>
          </div>
        </header>

        <div className="border-b bg-estado-info-bg px-5 py-3 text-xs text-estado-info">
          Para continuar al portal SICEN debes leer y aceptar las condiciones
          actuales. Mantén abierta la barra de desplazamiento para revisar el
          contenido completo.
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-5 py-4"
        >
          <CondicionesUsoContenido />
          <div className="mt-2 text-center text-[11px] text-fg-faint">— Fin del documento —</div>
        </div>

        <footer className="border-t bg-surface-2 px-5 py-4">
          <label
            className={`flex items-start gap-3 rounded-lg border p-3 text-sm transition ${
              leidoHastaElFinal
                ? "border-edge-strong bg-surface text-fg"
                : "cursor-not-allowed border-edge bg-surface-2 text-fg-faint"
            }`}
          >
            <input
              type="checkbox"
              checked={acepto}
              disabled={!leidoHastaElFinal || aceptar.isPending}
              onChange={(e) => setAcepto(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0"
            />
            <span>
              He leído y acepto el <strong>Aviso de Privacidad</strong> de Normalización
              Profesional de México, S.C., de conformidad con la legislación aplicable.
              {!leidoHastaElFinal && (
                <span className="mt-1 block text-[11px] text-estado-warn">
                  Desplázate hasta el final del documento para habilitar la aceptación.
                </span>
              )}
            </span>
          </label>

          {error && (
            <div className="mt-2 flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="mt-3 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={cerrarSesion}
              className="inline-flex items-center justify-center gap-1 rounded-lg border border-edge-strong px-4 py-2 text-sm font-medium text-fg-muted hover:bg-surface-2"
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesión
            </button>
            <button
              type="button"
              onClick={() => aceptar.mutate(q.data!.version)}
              disabled={!acepto || aceptar.isPending}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
            >
              <Check className="h-4 w-4" />
              {aceptar.isPending ? "Registrando…" : "Aceptar y continuar"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
